function About() {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-slate-950 to-cyan-900/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-400" data-testid="about-title">
            About M31 NanoX
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Pioneering the future of advanced materials technology
          </p>
        </div>
      </section>
      
      {/* Company Overview */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1576141546153-3e04370b5ff7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxuYW5vdGVjaG5vbG9neXxlbnwwfHx8fDE3NzAxNzUyNDJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Laboratory"
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Who We Are</h2>
              <p className="text-gray-300 mb-4 leading-relaxed">
                M31 NanoX is a leading provider of advanced nanomaterials and battery materials, serving researchers, manufacturers, and innovators worldwide. Our name is inspired by the Andromeda Galaxy (M31), symbolizing our vision to reach for the stars through material science innovation.
              </p>
              <p className="text-gray-300 mb-4 leading-relaxed">
                With decades of combined expertise in nanotechnology and materials engineering, we specialize in producing ultra-high purity materials that enable breakthrough applications in energy storage, electronics, aerospace, and beyond.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Our state-of-the-art facilities and rigorous quality control processes ensure that every product meets the highest standards of purity, consistency, and performance.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Vision Section */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-gradient-to-br from-blue-900/20 to-cyan-900/20 p-10 rounded-2xl border border-blue-500/20" data-testid="vision-section">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">Our Vision</h2>
              <p className="text-gray-300 leading-relaxed">
                To be the global leader in advanced nanomaterials and battery materials, powering the technologies that will shape our future. We envision a world where material science breakthroughs enable sustainable energy solutions, revolutionary electronics, and space exploration capabilities.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-cyan-900/20 to-blue-900/20 p-10 rounded-2xl border border-cyan-500/20" data-testid="mission-section">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">Our Mission</h2>
              <p className="text-gray-300 leading-relaxed">
                To deliver the highest quality nanomaterials and battery materials with uncompromising standards of purity and performance. We are committed to supporting innovation through exceptional products, technical expertise, and customer service that exceeds expectations.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Values Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-400">The principles that guide everything we do</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-blue-500/50 transition-all">
              <h3 className="text-xl font-bold text-white mb-3">Excellence</h3>
              <p className="text-gray-400 text-sm">Unwavering commitment to quality and precision in every product</p>
            </div>
            
            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-cyan-500/50 transition-all">
              <h3 className="text-xl font-bold text-white mb-3">Innovation</h3>
              <p className="text-gray-400 text-sm">Continuously pushing boundaries in material science</p>
            </div>
            
            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-blue-500/50 transition-all">
              <h3 className="text-xl font-bold text-white mb-3">Integrity</h3>
              <p className="text-gray-400 text-sm">Transparent practices and honest partnerships</p>
            </div>
            
            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-cyan-500/50 transition-all">
              <h3 className="text-xl font-bold text-white mb-3">Sustainability</h3>
              <p className="text-gray-400 text-sm">Responsible manufacturing for a better future</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Team Section */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Our Expertise</h2>
            <p className="text-xl text-gray-400">Backed by leading scientists and engineers</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <img 
                src="https://images.unsplash.com/photo-1569852573858-03c9e2877792?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxuYW5vdGVjaG5vbG9neXxlbnwwfHx8fDE3NzAxNzUyNDJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Research Team"
                className="w-full h-64 object-cover rounded-xl mb-4"
              />
              <h3 className="text-xl font-bold text-white mb-2">Research & Development</h3>
              <p className="text-gray-400">Ph.D.-level scientists advancing nanomaterial synthesis</p>
            </div>
            
            <div className="text-center">
              <img 
                src="https://images.unsplash.com/photo-1576141546153-3e04370b5ff7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxuYW5vdGVjaG5vbG9neXxlbnwwfHx8fDE3NzAxNzUyNDJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Quality Control"
                className="w-full h-64 object-cover rounded-xl mb-4"
              />
              <h3 className="text-xl font-bold text-white mb-2">Quality Control</h3>
              <p className="text-gray-400">Rigorous testing and certification processes</p>
            </div>
            
            <div className="text-center">
              <img 
                src="https://images.unsplash.com/photo-1656331797721-b593b8f00297?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxuYW5vdGVjaG5vbG9neXxlbnwwfHx8fDE3NzAxNzUyNDJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Technical Support"
                className="w-full h-64 object-cover rounded-xl mb-4"
              />
              <h3 className="text-xl font-bold text-white mb-2">Technical Support</h3>
              <p className="text-gray-400">Expert guidance for your specific applications</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;